import { handleActions} from 'redux-actions';
import endpoints from '../../../config/endpoints';
import http from '../../shared/http';
import intl from '../../shared/intl';
import { LOCATION_CHANGE, push } from 'react-router-redux';
import { SubmissionError, reset } from 'redux-form';
import { create as createPolling } from '../../../../shared/js/intervals';

import { REFETCH_ALL_DATA } from '../../root';
import { actions as rootActions } from '../../root';
import { actions as ticketsListActions, UPLOAD_SUCCESS as UPLOAD_WHEN_CREATE } from '../list';

import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import filter from 'lodash/filter';

import { enum as threadCategoriesEnum } from '../../../../../enums/thread-categories';

// action types
const TICKET_FETCH_DATA_SUCCESS = 'ticket/fetch-data-success';
const TICKET_FETCH_DATA_ERROR = 'ticket/fetch-data-error';

const TOGGLE_PROGRESS_BAR = 'ticket/toggle-progress-bar';

const UPLOAD_SUCCESS = 'ticket/upload-success';
const UPLOAD_ERROR = 'ticket/upload-error';
const ADD_FILE_TO_UPLOAD = 'ticket/add-file-to-upload';
const REMOVE_FILE_FROM_UPLOAD = 'ticket/remove-file-from-upload';
const SET_LARGE_FILE_ERROR = 'ticket/set-too-large-file-error';

const CREATE_ANSWER_SUCCESS = 'answer/create-ticket-success';
const CREATE_ANSWER_ERROR = 'answer/create-ticket-error';

const TICKET_CLOSE_SUCCESS = 'ticket/close-success';
const TICKET_CLOSE_ERROR = 'ticket/close-error';

const TICKET_RATING_SUCCESS = 'ticket/ticket-rating-success';
const TICKET_RATING_ERROR = 'ticket/ticket-rating-error';

const CREATE_TICKET_SUCCESS = 'ticket/create-ticket-success';
const CREATE_TICKET_ERROR = 'ticket/create-ticket-error';

const threadPolling = createPolling('ticket', {
	newMessages: { interval: 5000 }
});

// initialState state
const initialState = {
	data: null,
	form: {
		fields: {
			text: ''
		},
		formName: 'answerTicketForm',
		fileInputKey: +new Date()
	},
	error: null,
	showProgressBar: false,
	filesToUpload: [],
	uploadError: false
};

// actions
export const actions = {
	fetchData: props => dispatch => {
		const ticketId = props.params && props.params.ticketId;
		const request = http({
			url: `${endpoints.TICKETS.ALL}/${ticketId}`,
			method: 'get',
			actions: [
				TICKET_FETCH_DATA_SUCCESS,
				TICKET_FETCH_DATA_ERROR
			]
		});

		request(dispatch).then(action => {
			if (action.type === TICKET_FETCH_DATA_SUCCESS) {
				dispatch(rootActions.fetchUpdates());
				threadPolling.actions.start('ticket', 'newMessages', actions.pollThread({params: { ticketId }}), dispatch);
			}
		});
	},

	pollThread: props => dispatch => {
		const ticketId = props.params && props.params.ticketId;
		const request = http({
			url: `${endpoints.TICKETS.ALL}/${ticketId}`,
			method: 'get',
			actions: [
				TICKET_FETCH_DATA_SUCCESS,
				TICKET_FETCH_DATA_ERROR
			]
		});

		request(dispatch);
	},

	addFileToUpload: file => (dispatch, getState) => {
		if (!file) {
			return;
		}

		dispatch({
			type: ADD_FILE_TO_UPLOAD,
			payload: file
		});

		dispatch({
			type: SET_LARGE_FILE_ERROR,
			payload: isAtachmentsExceedsLimit(getState().ticket.filesToUpload)
		});
	},

	removeFileFromUpload: (fileIndex) => (dispatch, getState) => {
		dispatch({
			type: REMOVE_FILE_FROM_UPLOAD,
			payload: fileIndex
		});

		dispatch({
			type: SET_LARGE_FILE_ERROR,
			payload: isAtachmentsExceedsLimit(getState().ticket.filesToUpload)
		});
	},

	uploadFiles: (threadId, postId, filesToUpload, dispatch) => {
		const data = new FormData();
		forEach(filesToUpload, (file, i) => {
			data.append(`file${i}`, file);
		});

		const request = http({
			url: `${endpoints.TICKETS.ALL}/${postId}/attachments`,
			method: 'put',
			actions: [
				UPLOAD_SUCCESS,
				UPLOAD_ERROR
			],
			data
		});

		dispatch(actions.toggleProgressBar());

		request(dispatch).then(action => {
			dispatch(actions.toggleProgressBar());
			dispatch(actions.refetchDataAndClearForm(threadId));
		});
	},

	createAnswer: data => (dispatch, getState) => {
		const state = getState();
		if (isAtachmentsExceedsLimit(state.ticket.filesToUpload)) {
			return;
		}

		const { id, offer_id } = state.ticket.data;

		const request = http({
			url: `${endpoints.TICKETS.ALL}/${id}`,
			method: 'post',
			actions: [
				CREATE_ANSWER_SUCCESS,
				CREATE_ANSWER_ERROR
			],
			data: {
				...data,
				thread_id: id,
				offer_id
			}
		});

		dispatch(actions.toggleProgressBar());

		return request(dispatch)
			.then(action => {
				dispatch(actions.toggleProgressBar());

				if (action.type === CREATE_ANSWER_SUCCESS) {
					const postId = action.payload.data.id;
					const filesToUpload = state.ticket.filesToUpload;

					if (!isEmpty(filesToUpload)) {
						actions.uploadFiles(id, postId, filesToUpload, dispatch);
					} else {
						dispatch(actions.refetchDataAndClearForm(id));
					}
				}

				if (action.type === CREATE_ANSWER_ERROR && action.payload.response.status === 400) {
					const validationsErrors = {};
					const responseErrors = action.payload.response.data;

					forEach(responseErrors, value => {
						const key = value.dataPath.slice(1);
						validationsErrors[key] = value.message;
					});

					throw new SubmissionError(validationsErrors);
				}
			});
	},

	closeTicket: () => (dispatch, getState) => {
		const { id } = getState().ticket.data;

		const request = http({
			url: `${endpoints.TICKETS.ALL}/${id}/close`,
			method: 'put',
			actions: [
				TICKET_CLOSE_SUCCESS,
				TICKET_CLOSE_ERROR
			]
		});

		dispatch(actions.toggleProgressBar());

		request(dispatch).then(action => {
			dispatch(actions.toggleProgressBar());
			dispatch(actions.fetchData({params: {ticketId: id}}));
		});
	},

	threadVote: (rating) => (dispatch, getState) => {
		const id = getState().ticket.data.id;

		dispatch({ type: TOGGLE_PROGRESS_BAR });

		const request = http({
			url: `${endpoints.TICKETS.ALL}/vote`,
			method: 'put',
			actions: [
				TICKET_RATING_SUCCESS,
				TICKET_RATING_ERROR
			],
			data: {
				id,
				rating
			}
		});

		request(dispatch).then((action)=>{
			dispatch({ type: TOGGLE_PROGRESS_BAR });
			dispatch({ type: TICKET_RATING_SUCCESS, payload: { id, rating } });
		});
	},

	toggleProgressBar: () => ({
		type: TOGGLE_PROGRESS_BAR
	}),

	refetchDataAndClearForm: (ticketId) => (dispatch, getState) => {
		threadPolling.actions.stop('ticket', 'newMessages');
		dispatch(reset(getState().ticket.form.formName));
		dispatch(actions.fetchData({params: { ticketId }}));
	},

	createTicket: formData => (dispatch, getState) => {
		const {data: { category, priority, title }, filesToUpload} = getState().ticket;
		const { text } = formData;

		const categoryToSubmit =
			category === threadCategoriesEnum.admin_ticket ? threadCategoriesEnum.affiliate_question : category;

		if (isAtachmentsExceedsLimit(filesToUpload)) {
			return;
		}

		const request = http({
			url: endpoints.TICKETS.ALL,
			method: 'post',
			actions: [CREATE_TICKET_SUCCESS, CREATE_TICKET_ERROR],
			data: {
				category: categoryToSubmit,
				priority,
				title,
				text
			}
		});

		dispatch(actions.toggleProgressBar());

		return request(dispatch).then(action => {
			if (action.type === CREATE_TICKET_SUCCESS) {
				const threadId = action.payload.data.id;
				if (!isEmpty(filesToUpload)) {
					const postId = action.payload.data.posts[0].id;

					ticketsListActions.uploadFiles(postId, filesToUpload, dispatch).then(action => {
						if (action.type === UPLOAD_WHEN_CREATE) {
							dispatch(actions.redirectToNewTicketsList(threadId));
						}
					});

					return;
				}

				dispatch(actions.redirectToNewTicketsList(threadId));

			}

			if (action.type === CREATE_TICKET_ERROR && action.payload.response.status === 400) {
				dispatch(actions.toggleProgressBar());
				const validationsErrors = {};
				const responseErrors = action.payload.response.data;

				forEach(responseErrors, value => {
					const key = value.dataPath.slice(1);
					validationsErrors[key] = value.message;
				});

				throw new SubmissionError(validationsErrors);
			}
		});
	},

	redirectToNewTicketsList: (threadId) => dispatch => {
		setTimeout(() => dispatch(push(`/tickets/${threadId}/`)), 200);
	}

};

// reducers
const reducers = {
	[LOCATION_CHANGE]: (state, action) => {
		threadPolling.actions.stop('ticket', 'newMessages');
		if (
			action.payload.pathname.indexOf('tickets') !== -1
		) {
			return {
				...state,
				data: null,
				showProgressBar: false,
				filesToUpload: [],
				uploadError: false,
				error: false
			};
		}
		return state;
	},
	[TICKET_FETCH_DATA_SUCCESS]: (state, action) => {
		return {
			...state,
			data: action.payload.data,
			error: false,
			showProgressBar: false
		};
	},
	[TICKET_FETCH_DATA_ERROR]: (state, action) => ({
		...state,
		error: true
	}),
	[ADD_FILE_TO_UPLOAD]: (state, action) => {
		let filesToUpload = [].concat(state.filesToUpload);
		filesToUpload.push(action.payload);
		return {
			...state,
			filesToUpload: filesToUpload,
			form: {
				...state.form,
				fileInputKey: +new Date()
			}
		};
	},
	[REMOVE_FILE_FROM_UPLOAD]: (state, action) => {
		let filesToUpload = filter(state.filesToUpload, (f, i) => i != action.payload);
		return {
			...state,
			filesToUpload: filesToUpload,
			form: {
				...state.form,
				fileInputKey: +new Date()
			}
		};
	},
	[UPLOAD_SUCCESS]: (state, action) => {
		return {...state, filesToUpload: [], data: null};
	},
	[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
	[REFETCH_ALL_DATA]: () => initialState,
	[SET_LARGE_FILE_ERROR]: (state, action) => {
		return { ...state, uploadError: action.payload};
	},
	[TICKET_RATING_SUCCESS]: (state, action) => {
		return {
			...state,
			data: {
				...state.data,
				rating: action.payload.rating
			}
		};
	}

};

export default handleActions(reducers, cloneDeep(initialState));

const isAtachmentsExceedsLimit = files => {
	let fileSizeSum = 0;
	forEach(files, (file, i) => {
		fileSizeSum += file.size;
	});
	return fileSizeSum > 5*1024*1024;
};

// selectors
export const selectors = {
	getData: state => state.ticket.data,
	getFormData: state => {
		return ({
			initialValues: state.ticket.form.fields,
			filesToUpload: state.ticket.filesToUpload,
			errors: state.ticket.validate,
			uploadError: state.ticket.uploadError,
			fileInputKey: state.ticket.form.fileInputKey,
			isFromAdmin: state.app.userData.general.is_from_admin
		});
	},
	getProgressBar: state => state.ticket.showProgressBar,
	getUserInfo: state => state.app.userData,
	getError: state => state.ticket.error,
	getIntl: state => intl(state)
};



// WEBPACK FOOTER //
// ./frontend/cabinets/modules/tickets/item/index.js